import { useState } from 'react';
import axios from 'axios';
import { formatDependencies } from '../utils/aircraftLifecycleUtils';
import { buildComponentFormDataFromRecord } from '../utils/componentFormData';

/**
 * Custom hook for managing component operations
 * Handles CRUD operations and form state
 */
export const useComponentHandlers = (selectedAircraft, assemblies, fetchComponents) => {
  const [componentFormOpen, setComponentFormOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [componentFormData, setComponentFormData] = useState({});

  const handleAddComponent = () => {
    if (assemblies.length === 0) {
      alert('Please add an assembly first before adding components');
      return;
    }
    setSelectedComponent(null);
    setComponentFormData({
      aircraftId: selectedAircraft?._id || '',
      assemblyId: assemblies.length === 1 ? assemblies[0]._id : '',
      ataChapterId: '',
      partId: '',
      partNumber: '',
      partName: '',
      description: '',
      serialNumber: '',
      position: '',
      manufacturerId: '',
      monitorTypeId: '',
      installedOnDate: '',
      licenseId: '',
      workOrderNumber: '',
      notes: '',
      asReceivedEvidenceFrom: '',
      isInitialLoad: false,
      isActive: true,
      timeSinceRecords: []
    });
    setComponentFormOpen(true);
  };

  const handleEditComponent = async (component) => {
    setSelectedComponent(component);
    setComponentFormData(await buildComponentFormDataFromRecord(component));
    setComponentFormOpen(true);
  };

  /** Open a new component form cloned from an existing component (e.g. MRO replace). */
  const handleCloneComponent = async (sourceComponent, overrides = {}) => {
    if (!sourceComponent) return;
    setSelectedComponent(null);
    const formData = await buildComponentFormDataFromRecord(sourceComponent, {
      serialNumber: '',
      code: '',
      isActive: true,
      installedOnDate: new Date().toISOString().split('T')[0],
      ...overrides
    });
    setComponentFormData(formData);
    setComponentFormOpen(true);
  };

  const handleSubmitComponent = async (data) => {
    try {
      if (selectedComponent) {
        await axios.put(`/api/components/${selectedComponent._id}`, data);
      } else {
        await axios.post('/api/components', data);
      }
      setComponentFormOpen(false);
      fetchComponents();
      setSelectedComponent(null);
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Error saving component');
    }
  };

  const handleDeleteComponent = async (component) => {
    const isArchived = component.isActive === false;
    const action = isArchived ? 'permanently delete' : 'archive';
    let reason = '';
    
    if (isArchived) {
      if (!window.confirm(`⚠️ WARNING: This will PERMANENTLY DELETE the archived component.\n\nThis action cannot be undone!\n\nAre you sure you want to proceed?`)) {
        return;
      }
      reason = 'Permanently deleted';
    } else {
      reason = window.prompt('Enter archive reason (optional):');
      if (reason === null) return; // User cancelled
      reason = reason || 'Archived by user';
      
      if (!window.confirm(`Are you sure you want to archive this component?`)) {
        return;
      }
    }

    try {
      await axios.delete(`/api/components/${component._id}`, {
        data: { reason }
      });
      fetchComponents();
      alert(isArchived ? 'Component permanently deleted successfully' : 'Component archived successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.message || `Error ${action}ing component`;
      if (error.response?.data?.hasDependencies) {
        const depMessage = formatDependencies(error.response.data.dependencies);
        alert(errorMessage + depMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleAddComponentToInventory = async (component) => {
    if (!component?._id) {
      alert('Invalid component selected');
      return;
    }

    const partLabel = component.partNumber || component.partId?.partNumber || component.code;
    if (!window.confirm(`Add component part "${partLabel}" to aircraft inventory with quantity 0?`)) {
      return;
    }

    try {
      const response = await axios.post(`/api/components/${component._id}/add-to-inventory`);
      if (response?.data?.merged) {
        alert(`Inventory row already existed for ${partLabel} on this aircraft. Linked details were refreshed.`);
      } else {
        alert(`Added ${partLabel} to aircraft inventory with quantity 0.`);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add component to inventory');
    }
  };

  const handleBackfillComponentsInventory = async () => {
    if (!selectedAircraft?._id) {
      alert('Select an aircraft first');
      return;
    }
    if (!window.confirm(`Run one-time component inventory backfill for aircraft ${selectedAircraft.registration}? Missing AIRCRAFT inventory rows will be created with quantity 0. Existing rows are skipped.`)) {
      return;
    }

    try {
      const response = await axios.post('/api/components/backfill-inventory', {
        aircraftId: selectedAircraft._id,
        confirm: true
      });
      const totals = response?.data?.totals || {};
      alert(
        `${response?.data?.message || 'Backfill completed.'}\n\nCreated: ${totals.created || 0}\nSkipped existing: ${totals.skippedExisting || 0}\nSkipped invalid: ${totals.skippedInvalid || 0}`
      );
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to run component inventory backfill');
    }
  };

  return {
    componentFormOpen,
    selectedComponent,
    componentFormData,
    setComponentFormData,
    setComponentFormOpen,
    setSelectedComponent,
    handleAddComponent,
    handleEditComponent,
    handleCloneComponent,
    handleSubmitComponent,
    handleDeleteComponent,
    handleAddComponentToInventory,
    handleBackfillComponentsInventory
  };
};
